/**
 * StageEditorView — pure presentation for the pipeline Stage editor (SE-5488,
 * restyled SE-6554).
 *
 * A CENTERED MODAL. It was a right-anchored drawer (bottom sheet on small
 * screens) until the SE-6554 design put it on the same footing as the Rules
 * panel: editing the shape of a pipeline is a focused task, not something you
 * consult while reading the board. The Modal atom is width-percentage based, so
 * it still adapts on a phone — but the bottom-sheet affordance is gone with the
 * drawer, which is the same trade RulesPanelView made and Raniel accepted.
 *
 * It keeps its scrim: a modal sits OVER the board and asks for attention, so
 * dimming is the correct affordance rather than a regression of the board's
 * no-dim rule (see the note in RulesPanelView and in the Drawer atom).
 *
 * Lists the board's stages in canonical order — each row carrying reorder,
 * rename, the Sensitive/Terminal flags, the time target, and delete — plus the
 * dashed add-stage row. The delete confirmation (block-on-cards / warn-on-rules)
 * is delegated to the DeleteStageDialog molecule, mounted here.
 *
 * The footer's Cancel / Save changes pair is the design's. Every edit in this
 * editor is written the moment it is made (a rename, a flag, a reorder, an add
 * and a delete each fire their own mutation), so neither button discards or
 * commits anything today — both dismiss. Converting to a draft-and-commit model
 * is a product decision, not a restyle: it needs local staged state, a batch
 * commit, and it would break the SE-6535 e2e contract that asserts a reorder
 * mutation fires on the move click.
 * @remarks View component — no business logic, hooks, or state.
 */

import { Plus, XIcon } from "lucide-react-native";
import { memo } from "react";
import { Platform } from "react-native";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import DeleteStageDialog from "../../molecules/DeleteStageDialog";
import StageEditorRow from "../../molecules/StageEditorRow";
import type { StageDeletionPlan } from "../../../utils/stageEditorOps";

/** One stage's presentational state (ordered). */
export interface StageEditorRowData {
  readonly stageId: string;
  readonly name: string;
  /** SE-6554: the stage's glossary line ("" renders no description). */
  readonly description: string;
  readonly isProtected: boolean;
  readonly isTerminal: boolean;
  /** SE-6554: cards currently in the stage (the count pill). */
  readonly cardCount: number;
  /** SE-6554: automation rules referencing the stage (the Guarded badge). */
  readonly ruleCount: number;
  /** SE-6554: the inline protection notice ("" renders none). */
  readonly protectionNotice: string;
  /** SE-5497: configured time-in-stage target in days (null = no target). */
  readonly timeTargetDays: number | null;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly isBusy: boolean;
  readonly isEditingName: boolean;
  readonly editNameValue: string;
}

/** Shared per-row handlers (routed by stageId). */
export interface StageEditorRowHandlers {
  readonly onStartRename: (stageId: string) => void;
  readonly onChangeName: (value: string) => void;
  readonly onSaveRename: (stageId: string) => void;
  readonly onCancelRename: () => void;
  readonly onToggleProtected: (stageId: string, next: boolean) => void;
  readonly onToggleTerminal: (stageId: string, next: boolean) => void;
  /** SE-5497: commit a stage's time target (null clears it). */
  readonly onChangeTarget: (stageId: string, next: number | null) => void;
  readonly onMoveUp: (stageId: string) => void;
  readonly onMoveDown: (stageId: string) => void;
  readonly onDelete: (stageId: string) => void;
}

/** The pending delete-confirmation state. */
export interface StageEditorDeleteState {
  readonly isOpen: boolean;
  readonly stageName: string;
  readonly plan: StageDeletionPlan | null;
  readonly isConfirming: boolean;
}

/** Props for {@link StageEditorView}. */
export interface StageEditorViewProps {
  /** Whether the editor is open. */
  readonly isOpen: boolean;
  /** Close handler. */
  readonly onClose: () => void;
  /**
   * The pipeline being edited, named in the header. Stages are board-scoped and
   * the modal covers the board, so without the name nothing on screen says
   * WHICH pipeline is about to be reshaped.
   */
  readonly pipelineName: string;
  /** Whether the stages are loading. */
  readonly loading: boolean;
  /** Whether a query errored. */
  readonly isError: boolean;
  /** The board's stages in canonical order. */
  readonly stages: readonly StageEditorRowData[];
  /** Per-row handlers. */
  readonly rowHandlers: StageEditorRowHandlers;
  /** Add-stage input value. */
  readonly addName: string;
  /** Add-stage input change. */
  readonly onAddNameChange: (value: string) => void;
  /** Add-stage submit. */
  readonly onAddSubmit: () => void;
  /**
   * The confirm button went down. Reported separately from the submit so the
   * container can tell a confirm-press apart from an abandoned draft on native,
   * where press-in lands before the input's blur. It does not on web — see the
   * focus guard on the confirm button (SE-6915).
   */
  readonly onAddSubmitPressIn: () => void;
  /** The confirm press finished or was aborted. */
  readonly onAddSubmitPressOut: () => void;
  /** Whether an add is in flight. */
  readonly isAdding: boolean;
  /** Whether the add row is expanded into its input + confirm. */
  readonly isAddOpen: boolean;
  /** Expand the collapsed dashed add row. */
  readonly onAddOpen: () => void;
  /**
   * The add input lost focus. Whether that discards the draft is the
   * container's call — it is a cancel for every blur except the one caused by
   * pressing the confirm.
   */
  readonly onAddBlur: () => void;
  /** The delete-confirmation state. */
  readonly deleteState: StageEditorDeleteState;
  /** Confirm the pending delete. */
  readonly onConfirmDelete: () => void;
  /** Cancel the pending delete. */
  readonly onCancelDelete: () => void;
}

/**
 * Cancels the mousedown's default so the browser leaves focus where it is.
 *
 * Typed by the single member it uses rather than a DOM event type, so the View
 * carries no dependency on ambient DOM lib types.
 * @param event - The confirm button's mousedown.
 * @param event.preventDefault - Cancels the browser's default focus transfer.
 */
const keepFocusOnAddInput = (event: { preventDefault: () => void }) => {
  event.preventDefault();
};

/**
 * Props that keep the add-stage confirm from stealing focus off the name input.
 *
 * On web the browser moves focus to whatever was pressed as part of the
 * `mousedown`, and preventing that event's default is the only thing that stops
 * it. With focus left on the input the input never blurs for a confirm click, so
 * the press resolves against a row that is still open and still holding the
 * typed name.
 *
 * The container's press-in/press-out guard cannot carry this on its own: through
 * the compiled RN-web stack the input's blur lands before the confirm's press-in
 * runs at all — the button unmounts with the collapsing row and neither press
 * callback ever fires (SE-6915, measured against the deployed build). The press
 * guard stays anyway for native, which has no focus transfer to cancel and no
 * mousedown to cancel it from.
 *
 * This is a platform CAPABILITY fork, not a layout one: `preventDefault` on a
 * mouse event exists only on web.
 *
 * Built by a helper rather than read at module scope so the platform is
 * resolved per render, which is also what makes the fork addressable from a
 * test.
 * @returns The mousedown guard on web; no extra props off it.
 */
const addConfirmFocusGuardProps = () =>
  Platform.OS === "web" ? { onMouseDown: keepFocusOnAddInput } : {};

/**
 * Render the body: spinner, error, or the ordered stage rows.
 * @param props - The editor props.
 * @returns The body node.
 */
const renderBody = (props: StageEditorViewProps) => {
  if (props.loading && props.stages.length === 0) {
    return (
      <Box className="items-center justify-center py-10">
        <Spinner />
      </Box>
    );
  }
  if (props.isError) {
    return (
      <Box className="items-center justify-center py-10">
        <Text variant="body" className="text-status-error">
          Could not load stages. Please try again.
        </Text>
      </Box>
    );
  }
  if (props.stages.length === 0) {
    return (
      <Text variant="caption" className="text-content-muted">
        No stages yet. Add one below.
      </Text>
    );
  }
  return (
    <Stack className="gap-2">
      {props.stages.map(stage => (
        <StageEditorRow
          key={stage.stageId}
          stageId={stage.stageId}
          name={stage.name}
          description={stage.description}
          isProtected={stage.isProtected}
          isTerminal={stage.isTerminal}
          cardCount={stage.cardCount}
          ruleCount={stage.ruleCount}
          protectionNotice={stage.protectionNotice}
          timeTargetDays={stage.timeTargetDays}
          isEditingName={stage.isEditingName}
          editNameValue={stage.editNameValue}
          isFirst={stage.isFirst}
          isLast={stage.isLast}
          isBusy={stage.isBusy}
          onStartRename={props.rowHandlers.onStartRename}
          onChangeName={props.rowHandlers.onChangeName}
          onSaveRename={props.rowHandlers.onSaveRename}
          onCancelRename={props.rowHandlers.onCancelRename}
          onToggleProtected={props.rowHandlers.onToggleProtected}
          onToggleTerminal={props.rowHandlers.onToggleTerminal}
          onChangeTarget={props.rowHandlers.onChangeTarget}
          onMoveUp={props.rowHandlers.onMoveUp}
          onMoveDown={props.rowHandlers.onMoveDown}
          onDelete={props.rowHandlers.onDelete}
        />
      ))}
    </Stack>
  );
};

/**
 * Render the Stage editor.
 * @param props - {@link StageEditorViewProps}.
 * @returns The modal.
 */
const StageEditorView = (props: StageEditorViewProps) => (
  <>
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      closeOnOverlayClick
      size="lg"
    >
      <ModalBackdrop />
      <ModalContent
        testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR}
        className="!p-0"
      >
        <ModalBody className="!my-0 p-0">
          <Stack
            direction="horizontal"
            className="items-center justify-between gap-4 border-b border-outline-default px-5 py-4"
          >
            {/* Unlike the Rules panel, the pipeline name is NOT accented here:
                the design draws the whole title in one weight and colour. */}
            <Text
              variant="title-sm"
              className="min-w-0 flex-1 text-content-primary"
            >
              {props.pipelineName === ""
                ? "Edit stages"
                : `Edit stages · ${props.pipelineName}`}
            </Text>
            <Pressable
              onPress={props.onClose}
              accessibilityLabel="Close stage editor"
              testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_CLOSE}
              className="size-8 shrink-0 items-center justify-center rounded-md bg-surface-raised"
            >
              <Icon as={XIcon} size="sm" className="text-content-secondary" />
            </Pressable>
          </Stack>

          <ScrollView
            className="max-h-120"
            showsVerticalScrollIndicator={false}
          >
            <Stack className="gap-4 px-5 pb-5 pt-4">
              <Text variant="caption" className="text-content-secondary">
                Rename, reorder, add or remove stages. Sensitive and terminal
                protection travels with the stage, not its name.
              </Text>

              {renderBody(props)}

              {/* Add a stage — progressive disclosure, as the design draws it.
                  Collapsed, the dashed row is ONLY the centred "+ Add stage"
                  call; the name input and its confirm appear once that is
                  pressed, and looking away collapses it again. A permanently
                  open input made the editor's last row look like a form the
                  user had left half-filled.

                  Losing focus is reported as a blur, not as a cancel, so the
                  container decides which blurs discard the draft. On web the
                  confirm additionally refuses the focus transfer outright, so
                  its click produces no blur to adjudicate (SE-6915). */}
              {props.isAddOpen ? (
                <Stack
                  direction="horizontal"
                  className="items-center gap-2 rounded-md border border-dashed border-outline-default px-3 py-2"
                >
                  <Input
                    className="flex-1 rounded-sm"
                    field={{
                      value: props.addName,
                      onChangeText: props.onAddNameChange,
                      placeholder: "New stage name",
                      autoFocus: true,
                      onBlur: props.onAddBlur,
                      onSubmitEditing: props.onAddSubmit,
                      testID: TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_ADD_INPUT,
                      accessibilityLabel: "New stage name",
                    }}
                  />
                  {/* A SOLID accent CTA, and the label reads on the accent
                      fill.

                      It was `variant="link"` with `labelTone="accent"`, which
                      rendered as a blue slab with an invisible label. The
                      Button atom applies its accent tone's SURFACE classes
                      (`bg-accent-primary`) for every variant — `link` only
                      strips the padding (`px-0`), it does not clear the fill —
                      so the button was painted accent-blue while its text was
                      told to be `text-accent-primary`: blue on blue. Nothing
                      about the confirm being a link was load-bearing, and this
                      is the same solid-accent CTA the footer's Save changes
                      already is, so it is now stated as one rather than
                      arriving there by accident.

                      The label is "Add", not "Add stage": beside an input the
                      button is narrow, and the shorter word fits inside it at
                      `size="sm"` without truncating. The stage editor is the
                      only surface in the app that paired `link` with `accent`,
                      so no other button changes. */}
                  <Button
                    label="Add"
                    variant="solid"
                    tone="accent"
                    size="sm"
                    icon={Plus}
                    iconSize="xs"
                    iconTone="on-accent"
                    labelTone="on-accent"
                    labelVariant="caption"
                    className="shrink-0 rounded-sm"
                    isDisabled={
                      props.isAdding || props.addName.trim().length === 0
                    }
                    onPress={props.onAddSubmit}
                    /* Native only, in practice. Press-in is documented to fire
                       on touch-down, ahead of any focus change, which is what
                       arms the container's guard there. On web it does NOT get
                       that far: the blur collapses the row and unmounts this
                       button before either press callback runs, so web relies
                       on the focus guard spread below instead (SE-6915). */
                    onPressIn={props.onAddSubmitPressIn}
                    onPressOut={props.onAddSubmitPressOut}
                    {...addConfirmFocusGuardProps()}
                    accessibilityLabel="Add stage"
                    testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_ADD_SUBMIT}
                  />
                </Stack>
              ) : (
                <Pressable
                  onPress={props.onAddOpen}
                  accessibilityLabel="Add stage"
                  accessibilityRole="button"
                  testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_ADD_OPEN}
                  /* The row is a full-width dashed rectangle with nothing but
                     a small centred label in it, so without a hover fill there
                     is no moment where it admits to being clickable — the
                     cursor crosses a large dead area before reaching the words.
                     `web:hover:` (pure CSS) rather than a JS `data-hover` state:
                     the JS hover strands on this surface and leaves the fill
                     stuck on after the pointer has left. */
                  className="web:hover:bg-surface-raised items-center justify-center rounded-md border border-dashed border-outline-default px-3 py-3"
                >
                  <Stack direction="horizontal" className="items-center gap-1">
                    <Icon
                      as={Plus}
                      size="2xs"
                      className="text-accent-primary"
                    />
                    <Text
                      variant="caption-strong"
                      className="text-accent-primary"
                    >
                      Add stage
                    </Text>
                  </Stack>
                </Pressable>
              )}

              <Text variant="micro" className="text-content-muted">
                New stages are working stages by default; mark
                Sensitive/Terminal via the row menu. Renames propagate to rules
                and history automatically.
              </Text>
            </Stack>
          </ScrollView>

          <Stack
            direction="horizontal"
            className="items-center justify-end gap-2 border-t border-outline-default px-5 py-4"
          >
            <Button
              label="Cancel"
              variant="outline"
              tone="secondary"
              size="sm"
              labelTone="muted"
              className="rounded-sm"
              onPress={props.onClose}
              accessibilityLabel="Close stage editor without further edits"
              testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_CANCEL}
            />
            {/* tone="accent", never solid `primary` — primary solid is WHITE in
                dark mode, which is not a CTA. */}
            <Button
              label="Save changes"
              variant="solid"
              tone="accent"
              size="sm"
              labelTone="on-accent"
              className="rounded-sm"
              onPress={props.onClose}
              accessibilityLabel="Save changes and close the stage editor"
              testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_SAVE}
            />
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>

    <DeleteStageDialog
      isOpen={props.deleteState.isOpen}
      stageName={props.deleteState.stageName}
      plan={props.deleteState.plan}
      isConfirming={props.deleteState.isConfirming}
      onConfirm={props.onConfirmDelete}
      onClose={props.onCancelDelete}
    />
  </>
);

StageEditorView.displayName = "StageEditorView";

export default memo(StageEditorView);
